import React, { useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";

const ManifestStatusActions = ({
    manifest,
    permissions,
    loading,
    onChangeState,
}) => {
    const [nextState, setNextState] = useState(null);
    const [observation, setObservation] = useState("");
    const [validationError, setValidationError] = useState("");

    const requestChange = (state) => {
        setNextState(state);
        setObservation("");
        setValidationError("");
    };

    const closeDialog = () => {
        if (loading) return;

        setNextState(null);
        setObservation("");
        setValidationError("");
    };

    const confirm = async () => {
        if (!nextState) return;

        const normalizedObservation = observation.trim();

        if (nextState === "COMPLETADO" && !normalizedObservation) {
            setValidationError(
                "Debe registrar una observación antes de finalizar el viaje."
            );
            return;
        }

        try {
            await onChangeState({
                nextState,
                observation:
                    nextState === "COMPLETADO"
                        ? normalizedObservation
                        : null,
            });

            closeDialog();
        } catch {
            // El padre muestra el mensaje de error.
            // El diálogo permanece abierto para no perder la observación.
        }
    };

    return (
        <>
            <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                flexWrap="wrap"
                useFlexGap
            >
                {permissions.canPlan && (
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={loading}
                        onClick={() => requestChange("PLANIFICADO")}
                    >
                        Planificar
                    </Button>
                )}

                {permissions.canStart && (
                    <Button
                        variant="contained"
                        color="success"
                        disabled={loading}
                        onClick={() => requestChange("EN_PROCESO")}
                    >
                        Iniciar viaje
                    </Button>
                )}

                {permissions.canComplete && (
                    <Button
                        variant="contained"
                        color="info"
                        disabled={loading}
                        onClick={() => requestChange("COMPLETADO")}
                    >
                        Finalizar viaje
                    </Button>
                )}

                {permissions.canCancel &&
                    manifest?.manest !== "CANCELADO" && (
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={loading}
                            onClick={() => requestChange("CANCELADO")}
                        >
                            Cancelar manifiesto
                        </Button>
                    )}
            </Stack>

            {manifest?.manest === "CANCELADO" && (
                <Alert severity="error" variant="outlined">
                    Este manifiesto fue cancelado. No debe editarse
                    operativamente.
                </Alert>
            )}

            <Dialog
                open={Boolean(nextState)}
                onClose={closeDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar cambio de estado</DialogTitle>

                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        ¿Desea cambiar el estado del manifiesto a{" "}
                        <strong>{nextState}</strong>?
                    </DialogContentText>

                    {nextState === "COMPLETADO" && (
                        <TextField
                            autoFocus
                            fullWidth
                            required
                            multiline
                            minRows={4}
                            maxRows={8}
                            label="Observación de finalización"
                            placeholder="Ingrese incidencias, comentarios o detalles relevantes del viaje."
                            value={observation}
                            onChange={(event) => {
                                setObservation(event.target.value);

                                if (validationError) {
                                    setValidationError("");
                                }
                            }}
                            error={Boolean(validationError)}
                            helperText={
                                validationError ||
                                `${observation.length}/2000 caracteres`
                            }
                            slotProps={{
                                htmlInput: {
                                    maxLength: 2000,
                                },
                            }}
                            disabled={loading}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeDialog} disabled={loading}>
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        color={
                            nextState === "CANCELADO"
                                ? "error"
                                : nextState === "COMPLETADO"
                                    ? "info"
                                    : "success"
                        }
                        onClick={confirm}
                        disabled={loading}
                    >
                        {loading ? "Procesando..." : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ManifestStatusActions;